import * as React from "react";
import { ArrowLeft } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { SiteFooter } from "@/components/SiteFooter";
import { StarField } from "@/components/StarField";

/** Gemeinsames Layout für Rechtstexte (Impressum, Datenschutz). */
export const LegalPage = ({ eyebrow = "Rechtliches", title, meta, sections }) => {
  React.useEffect(() => {
    window.scrollTo(0, 0);

    const robots = document.querySelector('meta[name="robots"]');
    const prevRobots = robots ? robots.getAttribute("content") : null;
    if (robots) robots.setAttribute("content", "noindex, nofollow, noarchive");

    const prevTitle = document.title;
    document.title = `${title} | Vision One`;

    return () => {
      if (robots && prevRobots !== null) robots.setAttribute("content", prevRobots);
      document.title = prevTitle;
    };
  }, [title]);

  return (
    <main className="min-h-screen bg-background">
      <Navbar />

      <section className="grain relative isolate overflow-hidden bg-[hsl(0_0%_0%)] pb-24 pt-36 sm:pt-40 lg:pb-32">
        <StarField count={35} className="-z-20 opacity-40" />
        <div
          aria-hidden
          className="absolute left-1/2 top-0 -z-20 h-[420px] w-[900px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,hsl(230_90%_50%/0.14),transparent)] blur-3xl"
        />

        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 lg:px-12">
          <a
            href="/"
            className="group inline-flex items-center gap-2 font-display text-xs font-light uppercase tracking-[0.24em] text-muted-foreground transition-colors duration-300 hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-0.5" />
            Zur Startseite
          </a>

          <div className="mt-10 max-w-3xl">
            <p className="font-display text-[11px] font-light uppercase tracking-[0.32em] text-primary-glow">
              {eyebrow}
            </p>
            <h1 className="mt-6 font-display text-3xl font-light leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              {title}
            </h1>
            <div className="mt-7 h-px w-32 bg-gradient-hairline" aria-hidden />
            {meta && (
              <p className="mt-6 text-xs font-light uppercase tracking-[0.2em] text-muted-foreground">
                {meta}
              </p>
            )}
          </div>

          <div className="mt-16 flex max-w-3xl flex-col gap-14">
            {sections.map((section) => (
              <article key={section.title} className="flex flex-col">
                <h2 className="font-display text-lg font-light leading-snug tracking-wide text-foreground sm:text-xl">
                  {section.title}
                </h2>
                <span aria-hidden className="mt-4 h-px w-16 bg-border" />

                <div className="mt-5 flex flex-col gap-4">
                  {section.blocks.map((block, index) =>
                    Array.isArray(block) ? (
                      <ul
                        key={`${section.title}-list-${index}`}
                        className="flex flex-col gap-2 border-l border-border/70 pl-5"
                      >
                        {block.map((item) => (
                          <li
                            key={item}
                            className="text-sm font-light leading-relaxed text-muted-foreground"
                          >
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p
                        key={`${section.title}-p-${index}`}
                        className="text-sm font-light leading-relaxed text-muted-foreground"
                      >
                        {block}
                      </p>
                    )
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};
